// Which chat API a session talks to: v2 reads Postgres, v1 reads Monday. Each app sets its rollout
// stage; where switching is allowed, the choice is stored per browser.

const STORAGE_KEY = 'odo_chat_api_version';

export type ChatApiVersion = 'v1' | 'v2';

export interface ChatRollout {
    defaultVersion: ChatApiVersion;
    canSwitch: boolean;
}

let rollout: ChatRollout = { defaultVersion: 'v1', canSwitch: true };

// Call before the first render: the first read is cached as the snapshot.
export function configureChatRollout(next: ChatRollout): void {
    rollout = next;
}

export function getChatRollout(): ChatRollout {
    return rollout;
}

// The version a user who may not switch must be moved to, or null when there is nothing to do.
export function forcedChatApiVersion(
    version: ChatApiVersion,
    { defaultVersion, canSwitch }: ChatRollout
): ChatApiVersion | null {
    return !canSwitch && version !== defaultVersion ? defaultVersion : null;
}

function isVersion(value: unknown): value is ChatApiVersion {
    return value === 'v1' || value === 'v2';
}

/**
 * Read the stored choice.
 *
 * Every access is guarded: storage throws outright in some privacy modes, and
 * a chat that refuses to render because it could not read a preference would
 * be a worse outage than the one this switch exists to escape.
 */
export function readChatApiVersion(): ChatApiVersion {
    try {
        const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
        return isVersion(stored) ? stored : rollout.defaultVersion;
    } catch {
        return rollout.defaultVersion;
    }
}

/**
 * Subscribers, so every reader agrees.
 *
 * The version is read in several places at once — each chat mount point picks
 * the service, and the toggle renders inside the tree those mount points own.
 * Without a shared store the button would update its own state and nothing
 * else, which looks exactly like a switch that does not work.
 */
const listeners = new Set<() => void>();
let current: ChatApiVersion | null = null;

export function subscribeChatApiVersion(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** Cached snapshot, required by useSyncExternalStore: it must be referentially stable. */
export function getChatApiVersionSnapshot(): ChatApiVersion {
    if (current === null) {
        current = readChatApiVersion();
    }
    return current;
}

export function writeChatApiVersion(version: ChatApiVersion): void {
    current = version;
    try {
        globalThis.localStorage?.setItem(STORAGE_KEY, version);
    } catch {
        // Ignored on purpose — see readChatApiVersion.
    }
    for (const listener of listeners) {
        listener();
    }
}

/**
 * Everything that has to be forgotten when the version changes.
 *
 * Not a nicety. A v1 message id is a Monday update id and a v2 message id is a
 * UUID, so a cache filled by one version and read by the other addresses
 * messages that do not exist: edits and deletes would 404 against ids from the
 * other world. The dashboard also PERSISTS its query cache to localStorage, so
 * a stale v1 feed survives a reload and the switch would look broken rather
 * than reverted.
 *
 * Callers pass their own invalidators; this only names what must go.
 */
export const CHAT_CACHE_PREFIXES = ['v1/patients', 'v2/conversations', 'unread-messages'] as const;
